<template>
    <div>
        <!-- Nothing in basket yet: single Add button -->
        <button v-if="quantity === 0"
                @click="add"
                :disabled="busy || max < 1"
                class="bg-stockbee-yellow text-stockbee-text font-semibold text-base px-6 py-3 rounded-[10px] hover:opacity-95 transition-opacity duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ busy ? 'Adding…' : 'Add to basket' }}
        </button>

        <!-- In basket: stepper -->
        <div v-else>
            <div class="flex items-center gap-4">
                <div class="inline-flex items-center border border-stockbee-border rounded-[10px] overflow-hidden">
                    <button @click="decrement" :disabled="busy"
                            aria-label="Remove one"
                            class="w-11 h-11 flex items-center justify-center text-2xl text-stockbee-heading hover:bg-stockbee-bg disabled:opacity-50">
                        &minus;
                    </button>
                    <span class="w-12 text-center font-bold text-lg text-stockbee-heading">{{ quantity }}</span>
                    <button @click="increment" :disabled="busy || quantity >= max"
                            aria-label="Add one"
                            class="w-11 h-11 flex items-center justify-center text-2xl text-stockbee-heading hover:bg-stockbee-bg disabled:opacity-40 disabled:cursor-not-allowed">
                        +
                    </button>
                </div>
                <span class="text-sm font-medium text-green-700">
                    {{ quantity }} in your basket
                </span>
            </div>

            <p v-if="quantity >= max" class="text-xs text-stockbee-text-soft mt-2">
                That's all we have available right now.
            </p>

            <a href="/basket"
               class="inline-block mt-4 text-sm font-semibold text-stockbee-heading underline hover:text-stockbee-yellow">
                View basket &amp; checkout &rarr;
            </a>
        </div>

        <p v-if="error" class="text-sm text-red-700 mt-2">{{ error }}</p>
    </div>
</template>

<script>
export default {
    props: {
        sku: { type: String, required: true },
        // Max this customer can have of this SKU (min of stock and per-order cap).
        max: { type: Number, default: 1 },
        // How many are already in the basket on page load.
        initialQuantity: { type: Number, default: 0 },
        // Used for GA4 / Google Ads ecommerce events.
        itemName: { type: String, default: '' },
        price: { type: Number, default: 0 },
        currency: { type: String, default: 'GBP' },
    },
    data() {
        return {
            quantity: this.initialQuantity,
            busy: false,
            error: null,
        };
    },
    methods: {
        async add() {
            if (this.busy || this.quantity >= this.max) return;
            await this.post('/basket/add', { sku: this.sku, quantity: 1 }, () => {
                this.quantity += 1;
                this.fireEcommerce('add_to_cart', 1);
            });
        },
        increment() {
            this.add();
        },
        async decrement() {
            if (this.busy) return;
            if (this.quantity <= 1) {
                await this.post('/basket/remove', { sku: this.sku }, () => {
                    this.fireEcommerce('remove_from_cart', this.quantity);
                    this.quantity = 0;
                });
            } else {
                await this.post('/basket/update', { sku: this.sku, quantity: this.quantity - 1 }, () => {
                    this.quantity -= 1;
                    this.fireEcommerce('remove_from_cart', 1);
                });
            }
        },
        fireEcommerce(eventName, quantity) {
            if (typeof window.gtag !== 'function' || !this.itemName) return;
            window.gtag('event', eventName, {
                currency: this.currency,
                value: this.price * quantity,
                items: [{
                    item_id: this.sku,
                    item_name: this.itemName,
                    price: this.price,
                    quantity,
                }],
            });
        },
        async post(url, body, onSuccess) {
            this.busy = true;
            this.error = null;
            try {
                const { data } = await window.axios.post(url, body);
                if (data && data.ok === false) {
                    this.error = data.message || 'Could not update your basket.';
                    return;
                }
                onSuccess();
                window.dispatchEvent(new CustomEvent('basket-updated'));
            } catch (e) {
                this.error = e.response?.data?.message || 'Could not update your basket. Please try again.';
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
